import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ShowcaseVisual from "./ShowcaseVisual";
import MediaLightbox, { warmMedia } from "./MediaLightbox";
import { MediaVideo } from "./WorkMedia";
import { trackAction } from "./PageAnalytics";

// One piece of work: its stage, its title and one meta line, with no card
// around them. The title holds the tile's one target, stretched over the
// whole tile, so the stage, the title and the meta line all follow it and
// assistive tech meets a single named link or button. Apps and plugins also
// name their links in the meta line, which sit above the stretched target
// (see workIndex.js).

const NEW_TAB = " (opens in a new tab)";

function TileArt({ tile }) {
  if (tile.visual) {
    return (
      <ShowcaseVisual
        project={{ id: tile.id, href: tile.href, title: tile.title }}
        visual={tile.visual}
        linked={false}
      />
    );
  }
  const { media } = tile;
  const plate = media && media.background ? { "--pf-tile-plate": media.background } : undefined;
  if (media && media.type === "video") {
    return (
      <div className="pf-tile__art" style={plate} role="img" aria-label={media.alt || tile.title}>
        <MediaVideo src={media.src} aria-hidden="true" />
      </div>
    );
  }
  return (
    <div className="pf-tile__art" style={plate}>
      {media && <img src={media.src} alt={media.alt || ""} loading="lazy" />}
    </div>
  );
}

function TileTarget({ tile, onOpen }) {
  const track = () => trackAction("open_work_item", tile.id);
  if (tile.href && tile.external) {
    return (
      <a
        href={tile.href}
        className="pf-tile__target"
        target="_blank"
        rel="noopener noreferrer"
        onClick={track}
      >
        {tile.title}
        <span className="pf-visually-hidden">{NEW_TAB}</span>
      </a>
    );
  }
  if (tile.href) {
    return (
      <Link to={tile.href} className="pf-tile__target" onClick={track}>
        {tile.title}
      </Link>
    );
  }
  if (tile.gallery && tile.gallery.length) {
    const warm = () => warmMedia(tile.gallery[0]);
    return (
      <button
        type="button"
        className="pf-tile__target"
        aria-haspopup="dialog"
        onPointerEnter={warm}
        onPointerDown={warm}
        onFocus={warm}
        onClick={() => {
          track();
          onOpen();
        }}
      >
        {tile.title}
        <span className="pf-visually-hidden">: open images</span>
      </button>
    );
  }
  return tile.title;
}

function WorkTile({ tile }) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const { links = [], gallery = [] } = tile;
  const hasTarget = Boolean(tile.href || gallery.length);

  return (
    <article className={`pf-tile${hasTarget ? " pf-tile--linked" : ""}`}>
      <div className="pf-tile__frame">
        <div className="pf-tile__zoom">
          <TileArt tile={tile} />
        </div>
      </div>
      <h3 className="pf-tile__title">
        <TileTarget tile={tile} onOpen={() => setViewerOpen(true)} />
      </h3>
      <p className="pf-tile__meta">
        <span>{tile.meta}</span>
        {tile.external && !links.length && (
          <ArrowUpRight size={14} strokeWidth={1.75} aria-hidden="true" />
        )}
        {links.map((link) => (
          <React.Fragment key={link.href}>
            <span aria-hidden="true">·</span>
            <a
              href={link.href}
              className="pf-tile__link"
              target={link.external ? "_blank" : undefined}
              rel={link.external ? "noopener noreferrer" : undefined}
              aria-label={`${link.label}${link.external ? NEW_TAB : ""}`}
              onClick={() => trackAction("open_work_item", `${tile.id}:${link.short || link.label}`)}
            >
              {link.short || link.label}
              {link.external && <ArrowUpRight size={14} strokeWidth={1.75} aria-hidden="true" />}
            </a>
          </React.Fragment>
        ))}
      </p>
      {gallery.length > 0 && (
        <MediaLightbox
          open={viewerOpen}
          items={gallery}
          title={tile.title}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </article>
  );
}

export default WorkTile;
